import { Location } from 'history';

import {
  hasAlternateLangHref,
  getAlternateLangHref,
  getAlternateHrefs,
} from 'ha/modules/Hermes/selectors';
import { GlobalState } from 'ha/types/store';
import { Language } from 'ha/i18n';

import { STATE_NAME } from './constants';
import * as reducerSelectors from './reducer';

const getLanguageSwitcherState = (state: GlobalState) => state[STATE_NAME];

export const getCurrentLanguageCode = (state: GlobalState) =>
  reducerSelectors.getCurrentLanguageCode(getLanguageSwitcherState(state));

export const getAvailableLanguages = (state: GlobalState) =>
  reducerSelectors.getAvailableLanguages(getLanguageSwitcherState(state));

export const isLanguageAvailable = (state: GlobalState, lang: Language) =>
  reducerSelectors.isLanguageAvailable(getLanguageSwitcherState(state), lang);

export const getLocalisedLocations = (state: GlobalState) =>
  getLanguageSwitcherState(state).localisedLocations;

const getLocalisedLocationWithoutDefault = (
  state: GlobalState,
  lang: Language,
  currentLocation: Location,
) => {
  if (!isLanguageAvailable(state, lang)) {
    return undefined;
  }

  // let's try to find the url in the localised locations.
  const localisedLocation = reducerSelectors.getLocalisedLocation(
    getLanguageSwitcherState(state),
    lang,
  );

  if (localisedLocation) {
    return `${localisedLocation}${currentLocation.search}`;
  }

  // let's check if the language is available in Hermes
  if (hasAlternateLangHref(state, lang, currentLocation.pathname)) {
    return `${getAlternateLangHref(state, lang)}${currentLocation.search}`;
  }

  return undefined;
};

export const getLocalisedLocation = (
  state: GlobalState,
  lang: Language,
  currentLocation: Location,
) => {
  if (!isLanguageAvailable(state, lang)) {
    return `${currentLocation.pathname}${currentLocation.search}`;
  }

  const localisedLocation = getLocalisedLocationWithoutDefault(
    state,
    lang,
    currentLocation,
  );

  if (localisedLocation) {
    return localisedLocation;
  }

  // the url was not found. Let's create it ourselves
  const regexp = new RegExp(
    `^/(${getAvailableLanguages(state)
      .map(({ code }) => code)
      .join('|')})(/?.+)`,
  );
  const langForUrl = lang === 'en' ? '' : `/${lang}`;
  const match = currentLocation.pathname.match(regexp);
  const nextPathname = match
    ? `${langForUrl}${match[2]}`
    : `${langForUrl}${currentLocation.pathname}`;

  return `${nextPathname}${currentLocation.search}`;
};

export const getCurrentPageLanguages = (
  state: GlobalState,
  location: Location,
) => {
  let languages = getAvailableLanguages(state);

  const alternateHrefs = getAlternateHrefs(state, location.pathname);

  if (alternateHrefs.length > 0) {
    languages = languages.filter(lang =>
      getLocalisedLocationWithoutDefault(state, lang.code, location),
    );
  }

  const localisedLocations = getLocalisedLocations(state);

  if (localisedLocations.length > 0) {
    languages = languages.filter(lang =>
      localisedLocations.some(
        localisedLocation => localisedLocation.code === lang.code,
      ),
    );
  }

  return languages.map(lang => ({
    ...lang,
    localisedUrl: getLocalisedLocation(state, lang.code, location),
  }));
};
